import { ICellRendererParams } from 'ag-grid-community';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { AlertApiClientService } from '../../services/alert-api-client.service';

@Component({
  selector: 'app-frequency-renderer',
  templateUrl: './frequency-renderer.component.html',
  styleUrls: ['./frequency-renderer.component.scss'],
  standalone: false,
})
export class FrequencyRendererComponent implements OnInit, OnDestroy {
  private readonly alertApiClientService = inject(AlertApiClientService);

  frequency = 'WEEKLY';
  params!: ICellRendererParams;
  selectedDays: string[] = [];
  isMonthlyDelivery = false;

  private hasPendingChanges = false;

  private readonly MONTHLY_ONLY_PRODUCTS = ['TNW', 'IBNW', 'APPW', 'CNIT'];

  private frequencyUpdate$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.frequencyUpdate$
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.saveToBackend();
        this.hasPendingChanges = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    if (this.hasPendingChanges && this.params?.data) {
      this.saveToBackend();
      this.hasPendingChanges = false;
    }
    this.destroy$.complete();
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.hasPendingChanges = false;

    if (params.data) {
      this.frequency = (params.data.frequency?.type || 'WEEKLY').toUpperCase();
      this.selectedDays = params.data.frequency?.days || [];
      this.checkMonthlyDeliveryRequirement();
    }
  }

  onFrequencyChange(newFrequency: string): void {
    this.frequency = newFrequency;
    this.updateData();
    this.hasPendingChanges = true;
    this.frequencyUpdate$.next();
  }

  onSelectedDaysChange(days: string[]): void {
    this.selectedDays = days;
    this.updateData();
    this.hasPendingChanges = true;
    this.frequencyUpdate$.next();
  }

  private updateData(): void {
    if (this.params.node) {
      if (!this.params.data.frequency) {
        this.params.data.frequency = {
          type: this.frequency,
          days: [...this.selectedDays],
        } as any;
        return;
      }

      this.params.data.frequency.type = this.frequency;
      this.params.data.frequency.days = [...this.selectedDays];
    }
  }

  private saveToBackend(): void {
    if (!this.params?.data) {
      return;
    }

    const frequencyData = {
      ...this.params.data,
      frequency: {
        type: this.frequency,
        days: [...this.selectedDays],
      },
    };

    this.alertApiClientService.updateAlert(frequencyData).subscribe();
  }

  private checkMonthlyDeliveryRequirement(): void {
    const productsRaw = this.params.data?.products as string | undefined;
    if (!productsRaw) {
      this.isMonthlyDelivery = false;
      return;
    }

    const productCodes = productsRaw
      .split(',')
      .map((p: string) => p.trim().toUpperCase())
      .filter(Boolean);

    if (productCodes.length === 0) {
      this.isMonthlyDelivery = false;
      return;
    }

    const hasNonMonthlyProduct = productCodes.some(
      (code) => !this.MONTHLY_ONLY_PRODUCTS.includes(code),
    );
    this.isMonthlyDelivery = !hasNonMonthlyProduct;
  }
}
